import React, { useEffect, useState } from 'react';
import { Toast } from '@douyinfe/semi-ui';
import { API } from '../helpers';

const BASE = '/remote-api';
const WECHAT_ID = 'ota1245';
const QQ_ID = '2642977487';
const TAVERN_URL = 'https://jiuguan.ota1245.top/';
const BUY_URL = 'https://pay.ldxp.cn/shop/EFTZ83MX';

function formatAnnouncementTime(ts) {
  if (!ts) return '';
  const n = typeof ts === 'number' ? ts : Date.parse(ts);
  if (!n || Number.isNaN(n)) return String(ts);
  const d = new Date(n < 1e12 ? n * 1000 : n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normalizeAnnouncements(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item, idx) => {
        if (typeof item === 'string') {
          return { id: idx, title: '公告', content: item, time: '' };
        }
        return {
          id: item.id ?? idx,
          title: item.title || item.name || '公告',
          content: item.content || item.html || item.text || '',
          time: item.created_at || item.publish_time || item.time || item.updated_at || '',
        };
      })
      .filter((x) => x.content || x.title);
  }
  if (typeof raw === 'string') {
    return raw.trim() ? [{ id: 0, title: '系统公告', content: raw, time: '' }] : [];
  }
  return [];
}

async function copyId(text, label) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      Toast.success('已复制');
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    Toast.success('已复制');
  } catch {
    Toast.info(`${label}：${text}`);
  }
}

function openUrl(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

const Dashboard = ({ page = 'home', onOpenPage, variant = 'stack' }) => {
  const [noticeHtml, setNoticeHtml] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemName, setSystemName] = useState('砂糖酒馆');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [statusRes, noticeRes] = await Promise.all([
          API.get(`${BASE}/api/status`).catch(() => null),
          API.get(`${BASE}/api/notice`).catch(() => null),
        ]);
        if (cancelled) return;
        const status = statusRes?.data?.data || statusRes?.data || {};
        if (status.system_name) setSystemName(status.system_name);
        const fromStatus = normalizeAnnouncements(status.announcements);
        const noticeData = noticeRes?.data?.data ?? noticeRes?.data;
        if (typeof noticeData === 'string') {
          setNoticeHtml(noticeData);
        } else if (noticeData && typeof noticeData === 'object') {
          setNoticeHtml(noticeData.content || noticeData.html || '');
        }
        const fromNotice = normalizeAnnouncements(
          Array.isArray(noticeData) ? noticeData : noticeData?.list || noticeData?.items
        );
        setAnnouncements(fromStatus.length ? fromStatus : fromNotice);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const list =
    announcements.length > 0
      ? announcements
      : noticeHtml
        ? [{ id: 0, title: '系统公告', content: noticeHtml, time: '' }]
        : [];

  if (page === 'notice') {
    return (
      <div className="w-info-page">
        <div className="w-info-hero">
          <div className="w-info-kicker">NOTICE</div>
          <h2>系统公告</h2>
          <p>查看站点最新通知与说明</p>
        </div>
        {loading ? (
          <div className="w-info-empty">加载中…</div>
        ) : list.length === 0 ? (
          <div className="w-info-empty">
            <div className="w-info-empty-icon">📭</div>
            <div className="w-info-empty-title">暂无公告</div>
            <p>有更新时会显示在这里</p>
          </div>
        ) : (
          <div className="w-notice-list" style={{ marginTop: 16, gap: 12 }}>
            {list.map((item, idx) => (
              <div key={item.id} className={`w-notice-item ${idx === 0 ? 'w-notice-item-primary' : ''}`}>
                {idx === 0 && <div className="w-notice-badge">最新</div>}
                <div className="w-notice-title-row">
                  <div className="w-notice-title">{item.title}</div>
                  {item.time ? (
                    <div className="w-notice-time">{formatAnnouncementTime(item.time)}</div>
                  ) : null}
                </div>
                <div
                  className="w-notice-body"
                  dangerouslySetInnerHTML={{
                    __html: item.content || '<span class="w-notice-muted">无内容</span>',
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (page === 'contact') {
    return (
      <div className="w-info-page">
        <div className="w-info-hero">
          <div className="w-info-kicker">CONTACT</div>
          <h2>联系客服</h2>
          <p>点击账号即可复制</p>
        </div>
        <div className="w-contact-grid w-contact-ids" style={{ marginTop: 16, gap: 16 }}>
          <button
            type="button"
            className="w-contact-id-card wechat"
            onClick={() => copyId(WECHAT_ID, '微信')}
          >
            <span className="w-contact-id-label">微信</span>
            <strong className="w-contact-id-value">{WECHAT_ID}</strong>
            <span className="w-contact-id-tip">点击复制</span>
          </button>
          <button
            type="button"
            className="w-contact-id-card qq"
            onClick={() => copyId(QQ_ID, 'QQ')}
          >
            <span className="w-contact-id-label">QQ</span>
            <strong className="w-contact-id-value">{QQ_ID}</strong>
            <span className="w-contact-id-tip">点击复制</span>
          </button>
        </div>
      </div>
    );
  }

  if (page === 'tavern-free' || page === 'tavern-buy') {
    const isFree = page === 'tavern-free';
    return (
      <div className="w-info-page">
        <div className={`w-info-hero w-info-hero-tavern ${isFree ? 'free' : 'buy'}`}>
          <div className="w-info-kicker">{isFree ? 'TAVERN' : 'BUY'}</div>
          <h2>{isFree ? '酒馆' : '购买'}</h2>
          <p>{isFree ? '一键进入酒馆' : '一键前往购买'}</p>
        </div>
        <div className="w-tavern-panel" style={{ marginTop: 16 }}>
          <div className={`w-tavern-card ${isFree ? '' : 'pro'}`}>
            <div className="w-tavern-icon" style={{ width: 48, height: 48, placeItems: 'center', marginBottom: 12 }}>
              {isFree ? '🍶' : '⭐'}
            </div>
            <div className="w-tavern-title">{isFree ? '进入酒馆' : '立即购买'}</div>
            <ul className="w-tavern-list" style={{ marginTop: 12, marginBottom: 20 }}>
              {isFree ? (
                <>
                  <li>在线体验入口</li>
                  <li>点击下方按钮直接打开</li>
                </>
              ) : (
                <>
                  <li>充值 / 购买入口</li>
                  <li>点击下方按钮直接打开</li>
                </>
              )}
            </ul>
            <button
              type="button"
              className={`w-tavern-btn-xl ${isFree ? 'green' : 'primary'}`}
              onClick={() => openUrl(isFree ? TAVERN_URL : BUY_URL)}
            >
              {isFree ? '进入酒馆' : '立即购买'}
            </button>
            <div className="w-tavern-hint" style={{ marginTop: 12 }}>
              站点：{systemName}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const noticeCard = (
    <div className="w-dash-card w-dash-notice">
      <div className="w-dash-head">
        <div className="w-dash-ico">📢</div>
        <div className="w-dash-label">最新公告</div>
      </div>
      {loading ? (
        <div className="w-dash-body w-dash-muted">加载中…</div>
      ) : list.length === 0 ? (
        <div className="w-dash-body w-dash-muted" style={{ textAlign: 'center', padding: '8px 0' }}>
          <div className="w-dash-empty-icon" style={{ fontSize: 22 }}>
            📭
          </div>
          暂无公告
        </div>
      ) : (
        <div className="w-dash-body">
          <div className="w-dash-title">{list[0].title}</div>
          <div
            className="w-dash-text"
            style={{ fontSize: 13, overflow: 'hidden', maxHeight: variant === 'rail' ? 48 : 72 }}
            dangerouslySetInnerHTML={{ __html: list[0].content }}
          />
          {list[0].time ? (
            <div className="w-dash-time" style={{ marginTop: 6 }}>
              {formatAnnouncementTime(list[0].time)}
            </div>
          ) : null}
          <button
            type="button"
            className="w-dash-btn"
            style={{ marginTop: 8 }}
            onClick={() => onOpenPage && onOpenPage('notice')}
          >
            查看全部 →
          </button>
        </div>
      )}
    </div>
  );

  const contactCard = (
    <div className="w-dash-card w-dash-contact">
      <div className="w-dash-head">
        <div className="w-dash-ico">💬</div>
        <div className="w-dash-label">联系客服</div>
      </div>
      <div className="w-dash-contact-ids">
        <button type="button" className="w-id-chip wechat" onClick={() => copyId(WECHAT_ID, '微信')}>
          <span>微信</span>
          <b>{WECHAT_ID}</b>
          <em>点击复制</em>
        </button>
        <button type="button" className="w-id-chip qq" onClick={() => copyId(QQ_ID, 'QQ')}>
          <span>QQ</span>
          <b>{QQ_ID}</b>
          <em>点击复制</em>
        </button>
      </div>
    </div>
  );

  const tavernCards = (
    <div className={`w-dash-tavern-row ${variant === 'strip' ? 'is-strip' : ''}`}>
      <div className="w-dash-card w-dash-tavern free">
        <div className="w-dash-head">
          <div className="w-dash-ico">🍶</div>
          <div className="w-dash-label">酒馆</div>
        </div>
        <div className="w-dash-text" style={{ marginBottom: 12 }}>
          一键进入
        </div>
        <button type="button" className="w-dash-btn-xl soft-green" onClick={() => openUrl(TAVERN_URL)}>
          进入酒馆
        </button>
      </div>
      <div className="w-dash-card w-dash-tavern buy">
        <div className="w-dash-head">
          <div className="w-dash-ico">⭐</div>
          <div className="w-dash-label">购买</div>
        </div>
        <div className="w-dash-text" style={{ marginBottom: 12 }}>
          一键购买
        </div>
        <button type="button" className="w-dash-btn-xl primary" onClick={() => openUrl(BUY_URL)}>
          立即购买
        </button>
      </div>
    </div>
  );

  if (variant === 'strip') {
    return (
      <div className="w-dash w-dash-strip">
        <div className="w-dash-strip-notice">
          <span className="w-dash-ico">📢</span>
          <div className="w-dash-strip-text">
            {loading
              ? '公告加载中…'
              : list.length === 0
                ? '暂无公告'
                : (
                  <>
                    <b>{list[0].title}：</b>
                    <span dangerouslySetInnerHTML={{ __html: list[0].content }} />
                  </>
                )}
          </div>
          <button type="button" className="w-dash-btn" onClick={() => onOpenPage && onOpenPage('notice')}>
            更多
          </button>
        </div>
        <div className="w-dash-strip-actions">
          <button type="button" className="w-chip" onClick={() => onOpenPage && onOpenPage('contact')}>
            客服
          </button>
          <button type="button" className="w-chip" onClick={() => openUrl(TAVERN_URL)}>
            酒馆
          </button>
          <button type="button" className="w-chip active" onClick={() => openUrl(BUY_URL)}>
            购买
          </button>
          <button type="button" className="w-chip" onClick={() => onOpenPage && onOpenPage('models')}>
            模型广场
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'rail') {
    return (
      <div className="w-dash w-dash-rail">
        <div className="w-dash-card w-dash-notice">
          <div className="w-dash-head">
            <div className="w-dash-ico">📢</div>
            <div className="w-dash-label">公告 / 客服</div>
          </div>
          {loading ? (
            <div className="w-dash-body w-dash-muted">加载中…</div>
          ) : list.length === 0 ? (
            <div className="w-dash-body w-dash-muted">暂无公告</div>
          ) : (
            <div className="w-dash-body">
              <div className="w-dash-title">{list[0].title}</div>
              <div
                className="w-dash-text w-dash-text-full"
                dangerouslySetInnerHTML={{ __html: list[0].content }}
              />
            </div>
          )}
          <div className="w-dash-contact-ids w-dash-contact-ids-compact">
            <button type="button" className="w-id-chip wechat" onClick={() => copyId(WECHAT_ID, '微信')}>
              <span>微信</span>
              <b>{WECHAT_ID}</b>
              <em>复制</em>
            </button>
            <button type="button" className="w-id-chip qq" onClick={() => copyId(QQ_ID, 'QQ')}>
              <span>QQ</span>
              <b>{QQ_ID}</b>
              <em>复制</em>
            </button>
          </div>
        </div>
        <div className="w-dash-card w-dash-tavern-merged">
          <div className="w-dash-head">
            <div className="w-dash-ico">🍶</div>
            <div className="w-dash-label">快捷入口</div>
          </div>
          <div className="w-rail-actions">
            <button type="button" className="w-dash-btn-xl soft-green" onClick={() => openUrl(TAVERN_URL)}>
              进入酒馆
            </button>
            <button type="button" className="w-dash-btn-xl primary" onClick={() => openUrl(BUY_URL)}>
              立即购买
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'bottom') {
    return (
      <div className="w-dash w-dash-bottom">
        {noticeCard}
        {contactCard}
        {tavernCards}
      </div>
    );
  }

  return (
    <div className="w-dash w-dash-stack">
      {noticeCard}
      {contactCard}
      {tavernCards}
    </div>
  );
};

export default Dashboard;
